import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ApprovalInput } from "@avestate/shared";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

const Approval = z.object({
  id: z.string().uuid(),
  claim_key: z.string(),
  decision: z.enum(["approved", "rejected", "needs_revision"]),
  comment: z.string().nullable(),
  decided_by: z.string().uuid(),
  decided_at: z.string(),
  version_id: z.string().uuid().nullable(),
});
export type Approval = z.infer<typeof Approval>;

const ApprovalsResponse = z.object({ approvals: z.array(Approval) });

export function useApprovals(listingId: string | undefined) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["approvals", listingId],
    enabled: !!token && !!listingId,
    queryFn: () => apiFetch(`/api/v1/admin/listings/${listingId}/approvals`, token!, ApprovalsResponse),
  });
}

const ApprovalCreated = z.object({
  id: z.string().uuid(),
  decision: z.enum(["approved", "rejected", "needs_revision"]),
  decided_at: z.string(),
});

export function useCreateApproval(listingId: string) {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: (input: z.infer<typeof ApprovalInput>) => {
      if (!token) throw new Error("not authenticated");
      return apiFetch(`/api/v1/admin/listings/${listingId}/approvals`, token, ApprovalCreated, {
        method: "POST", body: input,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["approvals", listingId] });
      void qc.invalidateQueries({ queryKey: ["ai_suggestions", listingId] });
    },
  });
}
